import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import { track, getPageSource } from '@mc/track-event'
import { useTranslation } from '@mc/i18n'
import { getNumberOfInstructors } from '@mc/instructors'
import { useStaticData } from '@mc/static-data-react'
import type { Course } from '@mc/api'

import { PricingText } from 'components/PricingText'
import useSticky from 'components/Sticky/useSticky'

import { PERSONALIZED_STICKY_FOOTER_I18N_NAMESPACE } from '../i18n'

interface StickyFooterContentProps {
  SignUpCta: React.ComponentType<{ fullWidth?: boolean; className?: string }>
  instructorName?: string
}

const regex = /^\/(classes|series)\/[^/]+$/

export const PersonalizedStickyFooterContent = ({
  SignUpCta,
}: StickyFooterContentProps) => {
  const { course } = useStaticData<{
    course: Course & { displayTitle: string }
  }>()
  const router = useRouter()
  const stickyIsActive = useSticky()
  const alreadyTracked = useRef(false)
  const personalizedCopy = regex.test(router.asPath) && !!course?.displayTitle

  useEffect(() => {
    if (!alreadyTracked.current && stickyIsActive) {
      track('Personalized Sticky Footer Viewed', {
        copy: personalizedCopy ? 'personalized' : 'fallback',
        page_source: getPageSource(),
      })
      alreadyTracked.current = true
    }
  }, [personalizedCopy, stickyIsActive])

  if (personalizedCopy) {
    return (
      <StickyContent
        SignUpCta={SignUpCta}
        instructorName={course.displayTitle}
      />
    )
  }

  return <StickyContent SignUpCta={SignUpCta} />
}

const StickyContent = ({
  SignUpCta,
  instructorName,
}: StickyFooterContentProps) => {
  const { t } = useTranslation(PERSONALIZED_STICKY_FOOTER_I18N_NAMESPACE)
  const isPersonalizedCopy = !!instructorName

  return (
    <div className='container mc-py-4'>
      <div className='d-flex flex-column flex-sm-row justify-content-md-between align-items-md-center'>
        <div className='mc-mb-2 mc-mb-sm-0 mc-mr-0 mc-mr-md-2 mc-mr-lg-6'>
          <p
            className='mc-text--brand mc-text--uppercase mc-text--balance'
            style={{ fontSize: '28px' }}
          >
            {isPersonalizedCopy
              ? t('stickyFooter.personalized', {
                  instructorName,
                  numClasses: getNumberOfInstructors(),
                })
              : t('stickyFooter.fallback', {
                  numClasses: getNumberOfInstructors(),
                })}
          </p>
        </div>

        <div className='d-flex flex-column flex-sm-row align-items-md-center'>
          {/* Mobile and Desktop */}
          <div className='d-flex d-md-none d-lg-flex'>
            <PricingText
              translationKey='short'
              className='mc-mr-5 mc-mb-4 mc-mb-sm-0 mc-text-small mc-text--balance'
              hidePricingMicrocopyExperiment
            />
          </div>
          {/* Tablet */}
          <div className='d-none d-md-flex d-lg-none'>
            <PricingText
              translationKey='short'
              className='mc-mr-2 mc-text-small mc-text--nowrap'
              hidePricingMicrocopyExperiment
            />
          </div>

          <SignUpCta />
        </div>
      </div>
    </div>
  )
}
